import {
  ClientApplicationFragment,
  useClientApplicationsQuery,
} from "@graphql/client-applications/ClientApplications.g";
import { TenantedClientApplicationReviewStateChoices } from "@graphql/generated/graphql.g";
import {
  type ColumnDef,
  PaginationState,
  createColumnHelper,
} from "@tanstack/react-table";
import { SortingState } from "@tanstack/react-table";
import { useDebounce } from "@uidotdev/usehooks";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";

import { ClientApplicationReviewPanel } from "@components/Tables/ClientApplicationsTable/ClientApplicationReviewPanel/ClientApplicationReviewPanel";
import { ReviewStateTag } from "@components/Texts";

import { Box } from "@design-system/Box/Box";
import { Button } from "@design-system/Button/Button";
import { Table } from "@design-system/Table/Table";
import { Tag } from "@design-system/Tag/Tag";
import { Text } from "@design-system/Text/Text";

import { routes } from "@pages/routes";

import { withTimezoneLabel } from "@utils/datetime";
import { usePaginationWithParams } from "@utils/hooks/usePaginationParams";

const PAGE_SIZE = 7;
const SEARCH_DEBOUNCE_TIME = 400;

/** Default queue: still needs a review decision (not APPROVED / UNAPPROVED). */
const STILL_TO_REVIEW_STATES = [
  TenantedClientApplicationReviewStateChoices.Discovered,
  TenantedClientApplicationReviewStateChoices.Evaluating,
];

const columnHelper = createColumnHelper<ClientApplicationFragment>();

type Props = {
  initialPageIndex?: number;
  search?: string;
};

export const ApplicationsUnreviewedTable = ({
  initialPageIndex = 0,
  search,
}: Props) => {
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: initialPageIndex,
    pageSize: PAGE_SIZE,
  });
  // Newest discoveries first — L2 works the review queue by first-seen time
  const [sorting, setSorting] = useState<SortingState>([
    { id: "created_at", desc: true },
  ]);
  const [selectedApplicationForReview, setSelectedApplicationForReview] =
    useState<ClientApplicationFragment | null>(null);

  const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_TIME);
  const clientApplicationsQuery = useClientApplicationsQuery({
    first: pagination.pageSize,
    offset: pagination.pageIndex * pagination.pageSize,
    reviewState_In: STILL_TO_REVIEW_STATES,
    orderBy:
      sorting.length > 0
        ? `${sorting[0].desc ? "-" : ""}${sorting[0].id}`
        : "-created_at",
    resource_IsPrimaryResource: true,
  });
  const navigate = useNavigate();

  const handlePaginationChange = usePaginationWithParams(
    pagination,
    setPagination,
  );

  const isClientApplicationsLoading = clientApplicationsQuery.isLoading;
  const isClientApplicationsFetching = clientApplicationsQuery.isFetching;

  const totalCount =
    clientApplicationsQuery.data?.clientApplications?.totalCount ?? undefined;

  useEffect(() => {
    setPagination({ pageIndex: 0, pageSize: PAGE_SIZE });
  }, [debouncedSearch]);

  const data = useMemo(() => {
    return (
      clientApplicationsQuery.data?.clientApplications?.edges
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ?.map((edge: any) => edge?.node)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ?.filter((clientApplication: any) => !!clientApplication) ?? []
    );
  }, [clientApplicationsQuery.data]);

  const handleApplicationRecordClick = useCallback(
    (application: ClientApplicationFragment) => {
      navigate(
        routes.portal.clients.client.applications.application.record.$buildPath(
          {
            params: {
              clientId: application.client.id,
              applicationId: application.id,
            },
          },
        ),
        {
          viewTransition: true,
        },
      );
    },
    [navigate],
  );

  const handleReviewClick = useCallback(
    (application: ClientApplicationFragment) => {
      setSelectedApplicationForReview(application);
    },
    [],
  );

  const handleCloseReviewPanel = useCallback(() => {
    setSelectedApplicationForReview(null);
  }, []);

  const handleReviewSuccess = useCallback(() => {
    clientApplicationsQuery.refetch();
  }, [clientApplicationsQuery]);

  const columns = useMemo(
    () =>
      [
        columnHelper.accessor("client.name", {
          header: "Tenant",
          id: "client__name",
          cell: (props) => {
            return (
              <Table.CompanyCell
                name={props.getValue() ?? ""}
                skipLogo={true}
              />
            );
          },
          footer: undefined,
        }),
        columnHelper.accessor("resource.productName", {
          header: "Application",
          id: "resource__product_name",
          cell: (props) => {
            return (
              <Table.CompanyCell
                name={props.row.original.resource?.vendorName ?? ""}
                subtext={props.getValue() || undefined}
                signedLogoUrl={
                  props.row.original.resource?.publicResource
                    ?.signedLogoUrl || undefined
                }
                domain={
                  props.row.original.resource?.vendorDomainName ??
                  props.row.original.resource?.productDomainName ??
                  ""
                }
              />
            );
          },
          footer: undefined,
        }),
        columnHelper.accessor("createdAt", {
          header: withTimezoneLabel("First discovered"),
          id: "created_at",
          cell: (props) => {
            return <Table.DateCell date={new Date(props.getValue())} />;
          },
          footer: undefined,
        }),
        columnHelper.accessor("reviewState", {
          header: "Review state",
          id: "review_state",
          cell: (props) => {
            const value = props.getValue();
            return value ? <ReviewStateTag text={value} /> : null;
          },
          footer: undefined,
        }),
        columnHelper.accessor("resource.publicResource.category.parent.name", {
          header: "Category",
          enableSorting: false,
          id: "resource__category__parent__name",
          cell: (props) => {
            const parentName = props.getValue();
            const categoryName =
              props.row.original.resource?.publicResource?.category?.name;
            // Fallback to child category name if parent is not available
            const displayName = parentName || categoryName;
            return (
              <Box>
                <Tag color="secondary" text={displayName} />
              </Box>
            );
          },
          footer: undefined,
        }),
        columnHelper.accessor("employees.totalCount", {
          header: "Used by",
          id: "rg_total_employees",
          cell: (props) => {
            return <Text>{props.getValue() ?? 0}</Text>;
          },
          footer: undefined,
        }),
        columnHelper.display({
          id: "approvals",
          header: "",
          cell: (props) => {
            return (
              <Box gap={2} justify="center">
                <Button
                  size="sm"
                  variant="outlined"
                  onClick={() => handleReviewClick(props.row.original)}
                  stretch
                >
                  Review
                </Button>
              </Box>
            );
          },
          meta: {
            sticky: true,
            actions: true,
          },
        }),
      ] as ColumnDef<ClientApplicationFragment, string>[],
    [handleReviewClick],
  );

  const handlePageSizeChange = useCallback((pageSize: number) => {
    setPagination({ pageIndex: 0, pageSize });
  }, []);

  return (
    <>
      <Table<ClientApplicationFragment, string>
        columns={columns}
        data={data}
        rowCount={totalCount}
        onPaginationChange={handlePaginationChange}
        pagination={pagination}
        isLoading={isClientApplicationsLoading}
        isFetching={isClientApplicationsFetching}
        onPageSizeChange={handlePageSizeChange}
        sorting={sorting}
        showPageSizeDropdown={false}
        onSortingChange={setSorting}
        onRowClick={handleApplicationRecordClick}
        emptyPlaceholder="All good! Everything is reviewed."
      />

      <ClientApplicationReviewPanel
        application={selectedApplicationForReview}
        clientId={selectedApplicationForReview?.client.id ?? ""}
        isOpen={!!selectedApplicationForReview}
        onClose={handleCloseReviewPanel}
        onSuccess={handleReviewSuccess}
      />
    </>
  );
};
